import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { AlertTriangle, Plus } from 'lucide-react';
import AdminTabs from '../AdminTabs';
import { AdminHead, AdminPage, Badge, Banner, Button, ConfirmDialog } from '../kit';
import SkillList from './SkillList';
import SkillDetail from './SkillDetail';
import SkillEditor from './SkillEditor';
import ReflectionsTab from './ReflectionsTab';
import {
  ACTION_SUCCESS, STATUS_VALUES, countByStatus, dirtyFields, draftFromSkill, emptyDraft, errorMessage,
  filterSkills, kindOptions as buildKindOptions, moveSelection, neighbourId, plural, serializeDraft,
  statusMeta, validateDraft,
} from './skillsLogic';
import {
  createAiSkill, deleteAiSkill, getAiSkill, listAiSkills, loadScopeLookups, setAiSkillStatus, updateAiSkill,
} from './skillsApi';
import './skills.css';

const LIST_LIMIT = 500;
const TABS = [{ key: 'review', label: 'Review' }, { key: 'reflections', label: 'Reflections' }];
const EMPTY_LOOKUPS = { orgs: new Map(), locations: new Map(), orgOptions: [], locationOptions: [] };

/**
 * Admin → AI Skills.
 *
 * The assistant proposes playbooks after conversations where it struggled;
 * nothing reaches a model prompt until an admin approves it here. Review is a
 * two-pane queue (list + detail/editor); Reflections is the audit log of
 * every reflection run. Tab, status, and selected skill live in the URL
 * (?tab=reflections&status=approved&skill=<id>, skill=new for the editor).
 */
export default function AiSkillsPage() {
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') === 'reflections' ? 'reflections' : 'review';
  const status = STATUS_VALUES.includes(params.get('status')) ? params.get('status') : 'proposed';
  const selectedId = params.get('skill') || null;
  const isNew = selectedId === 'new';

  const updateParams = useCallback((patch, { replace = false } = {}) => {
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      Object.entries(patch).forEach(([k, v]) => { if (v == null || v === '') next.delete(k); else next.set(k, v); });
      return next;
    }, { replace });
  }, [setParams]);

  // ── Data ──────────────────────────────────────────────────────────────────
  const [skills, setSkills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [memoryStore, setMemoryStore] = useState(false);
  const [viewerId, setViewerId] = useState(null);
  const [lookups, setLookups] = useState(EMPTY_LOOKUPS);
  const [detail, setDetail] = useState({ id: null, reflection: null, loading: false });

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const res = await listAiSkills({ limit: LIST_LIMIT });
      setSkills(Array.isArray(res?.skills) ? res.skills : []);
      setMemoryStore(Boolean(res?.memoryStore));
      if (res?.viewerId) setViewerId(res.viewerId);
    } catch (err) {
      setLoadError(errorMessage(err, 'Failed to load AI skills'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    let alive = true;
    loadScopeLookups().then((l) => { if (alive) setLookups(l); }).catch(() => {});
    return () => { alive = false; };
  }, []);

  const replaceSkill = useCallback((skill) => {
    if (!skill?.id) return;
    setSkills((prev) => (prev.some((s) => s.id === skill.id) ? prev.map((s) => (s.id === skill.id ? skill : s)) : [skill, ...prev]));
  }, []);

  const selected = useMemo(
    () => (selectedId && !isNew ? skills.find((s) => s.id === selectedId) || null : null),
    [skills, selectedId, isNew]
  );

  // A deep link (or a reflection's "view") can point at a skill in another
  // status — follow it so the list shows what the detail shows.
  useEffect(() => {
    if (selected && selected.status !== status) updateParams({ status: selected.status }, { replace: true });
  }, [selected, status, updateParams]);

  // Full record + the reflection that proposed it.
  useEffect(() => {
    if (!selectedId || isNew) return undefined;
    let alive = true;
    setDetail({ id: selectedId, reflection: null, loading: true });
    getAiSkill(selectedId)
      .then((res) => {
        if (!alive) return;
        if (res?.skill) replaceSkill(res.skill);
        if (res?.viewerId) setViewerId(res.viewerId);
        setDetail({ id: selectedId, reflection: res?.reflection || null, loading: false });
      })
      .catch(() => { if (alive) setDetail({ id: selectedId, reflection: null, loading: false }); });
    return () => { alive = false; };
  }, [selectedId, isNew, replaceSkill]);

  // ── List filters ─────────────────────────────────────────────────────────
  const [query, setQuery] = useState('');
  const [kinds, setKinds] = useState([]);
  const counts = useMemo(() => countByStatus(skills), [skills]);
  const kindOpts = useMemo(() => buildKindOptions(skills), [skills]);
  const visible = useMemo(() => filterSkills(skills, { status, query, kinds }), [skills, status, query, kinds]);
  const visibleIds = useMemo(() => visible.map((s) => s.id), [visible]);

  // ── Review state ─────────────────────────────────────────────────────────
  const [note, setNote] = useState('');
  const [busyAction, setBusyAction] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [edit, setEdit] = useState(null); // { id, draft, initial, errors, saving }
  const [pendingLeave, setPendingLeave] = useState(null);
  const detailRef = useRef(null);

  useEffect(() => { setNote(''); setActionError(null); }, [selectedId]);

  useEffect(() => {
    if (isNew && edit?.id !== 'new') setEdit({ id: 'new', draft: emptyDraft(), initial: emptyDraft(), errors: {}, saving: false });
  }, [isNew, edit]);

  const editing = Boolean(edit && edit.id === selectedId);
  const dirty = editing ? dirtyFields(edit.draft, edit.initial) : [];
  const hasUnsaved = dirty.length > 0;

  useEffect(() => {
    if (!hasUnsaved) return undefined;
    const onBeforeUnload = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [hasUnsaved]);

  /** Run `fn` now, or after the admin agrees to discard unsaved edits. */
  const guard = useCallback((fn) => {
    if (hasUnsaved) { setPendingLeave(() => fn); return; }
    setEdit(null);
    fn();
  }, [hasUnsaved]);

  const select = (id, { replace = false } = {}) => {
    if (id === selectedId) return;
    guard(() => {
      updateParams({ skill: id }, { replace });
      if (id && detailRef.current && window.matchMedia?.('(max-width: 900px)').matches) {
        setTimeout(() => detailRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
      }
    });
  };

  const onMove = (delta) => {
    const next = moveSelection(visibleIds, selectedId, delta);
    if (next && next !== selectedId) guard(() => updateParams({ skill: next }, { replace: true }));
  };

  // ── Actions ──────────────────────────────────────────────────────────────
  const runStatus = async (skill, action) => {
    setBusyAction(action.key);
    setActionError(null);
    try {
      const res = await setAiSkillStatus(skill.id, action.to, note.trim() || null);
      const nextId = neighbourId(visibleIds, skill.id);
      replaceSkill(res?.skill || { ...skill, status: action.to });
      setNote('');
      toast.success(ACTION_SUCCESS[action.key] || 'Updated');
      // Stay in the queue: move on to the neighbour in the current list.
      updateParams({ skill: nextId }, { replace: true });
    } catch (err) {
      const msg = errorMessage(err, `Couldn't ${action.label.toLowerCase()} this skill`);
      setActionError(msg);
      toast.error(msg);
    } finally {
      setBusyAction(null);
    }
  };

  const onAction = (skill, action) => {
    if (action.key === 'edit') {
      setActionError(null);
      const d = draftFromSkill(skill);
      setEdit({ id: skill.id, draft: d, initial: d, errors: {}, saving: false });
    } else if (action.key === 'delete') {
      setConfirmDelete(true);
    } else if (action.to) {
      runStatus(skill, action);
    }
  };

  const doDelete = async () => {
    if (!selected) return;
    setBusyAction('delete');
    setActionError(null);
    try {
      await deleteAiSkill(selected.id);
      const nextId = neighbourId(visibleIds, selected.id);
      setSkills((prev) => prev.filter((s) => s.id !== selected.id));
      setConfirmDelete(false);
      toast.success('Skill deleted');
      updateParams({ skill: nextId }, { replace: true });
    } catch (err) {
      const msg = errorMessage(err, "Couldn't delete this skill");
      setConfirmDelete(false);
      setActionError(msg);
      toast.error(msg);
    } finally {
      setBusyAction(null);
    }
  };

  const patchDraft = (patch) => setEdit((e) => (e ? { ...e, draft: { ...e.draft, ...patch } } : e));

  const cancelEdit = () => guard(() => { if (isNew) updateParams({ skill: null }, { replace: true }); });

  const save = async () => {
    if (!edit) return;
    const errors = validateDraft(edit.draft);
    if (Object.keys(errors).length) { setEdit((e) => ({ ...e, errors })); return; }
    const payload = serializeDraft(edit.draft);
    setEdit((e) => ({ ...e, errors: {}, saving: true }));
    setActionError(null);
    try {
      if (edit.id === 'new') {
        // Hand-written skills go through the same review gate as the assistant's.
        const res = await createAiSkill({ ...payload, status: 'proposed' });
        const created = res?.skill;
        if (!created?.id) throw new Error('The server did not return the new skill.');
        replaceSkill(created);
        setEdit(null);
        toast.success('Skill created as proposed — approve it when you are ready');
        updateParams({ status: created.status || 'proposed', skill: created.id }, { replace: true });
      } else {
        const res = await updateAiSkill(edit.id, payload);
        if (res?.skill) replaceSkill(res.skill);
        setEdit(null);
        toast.success(res?.skill?.version ? `Saved as version ${res.skill.version}` : 'Saved');
      }
    } catch (err) {
      const msg = errorMessage(err, edit.id === 'new' ? "Couldn't create the skill" : "Couldn't save your changes");
      setEdit((e) => (e ? { ...e, saving: false } : e));
      setActionError(msg);
      toast.error(msg);
    }
  };

  // ── Render ───────────────────────────────────────────────────────────────
  const renderEditor = () => {
    const skill = isNew ? null : selected;
    const st = skill ? statusMeta(skill.status) : null;
    return (
      <>
        <div className="sk-detail-head">
          <div className="sk-detail-title">
            <h2>{isNew ? 'New skill' : 'Edit skill'}</h2>
            {st ? <Badge tone={st.tone}>{st.label}</Badge> : <Badge tone="warn">Proposed</Badge>}
          </div>
          <span className="sk-prov">
            {isNew
              ? 'It is created as proposed, so nothing reaches the assistant until you approve it.'
              : skill?.status === 'approved'
                ? `Approved — the assistant uses your changes from its next matching question. Saving creates version ${(skill.version || 1) + 1}.`
                : `Saving creates version ${(skill?.version || 1) + 1}.`}
          </span>
        </div>
        <div className="sk-detail-body">
          <SkillEditor
            draft={edit.draft}
            onChange={patchDraft}
            errors={edit.errors}
            lookups={lookups}
            disabled={edit.saving}
            isNew={isNew}
          />
          {actionError ? <Banner tone="bad" icon={AlertTriangle} title="Not saved">{actionError}</Banner> : null}
        </div>
        <footer className="ak-panel-foot sk-foot">
          {dirty.length
            ? <span className="ak-dirty">{plural(dirty.length, 'unsaved change')}</span>
            : <span className="sk-foot-help">No changes</span>}
          <div className="sk-foot-actions">
            <Button onClick={cancelEdit} disabled={edit.saving}>Cancel</Button>
            <Button variant="primary" onClick={save} disabled={edit.saving || (!isNew && !dirty.length)}>
              {edit.saving ? 'Saving…' : isNew ? 'Create proposal' : 'Save changes'}
            </Button>
          </div>
        </footer>
      </>
    );
  };

  const renderDetail = () => {
    if (editing) return renderEditor();
    if (!selectedId) {
      return (
        <div className="sk-detail-empty">
          <strong>Select a skill to review</strong>
          <span>Pick one from the list. Use ↑ and ↓ to move through it.</span>
        </div>
      );
    }
    if (!selected) {
      if (loading) return <div className="sk-detail-empty" aria-busy="true"><div className="ak-sk" style={{ width: 220 }} /></div>;
      return (
        <div className="sk-detail-empty">
          <strong>This skill no longer exists</strong>
          <span>It may have been deleted.</span>
          <Button onClick={() => updateParams({ skill: null }, { replace: true })}>Back to the list</Button>
        </div>
      );
    }
    return (
      <SkillDetail
        skill={selected}
        reflection={detail.id === selected.id ? detail.reflection : null}
        detailLoading={detail.id === selected.id && detail.loading}
        viewerId={viewerId}
        lookups={lookups}
        note={note}
        onNote={setNote}
        busyAction={busyAction}
        actionError={actionError}
        onAction={(a) => onAction(selected, a)}
      />
    );
  };

  return (
    <div>
      <AdminTabs />
      <AdminPage className="sk-page">
        <AdminHead
          title="AI Skills"
          subtitle="Playbooks the assistant has proposed. The assistant only uses the ones you approve."
          tabs={TABS}
          activeTab={tab}
          onTabChange={(key) => guard(() => updateParams({ tab: key === 'review' ? null : key }))}
          actions={(
            <Button icon={Plus} onClick={() => guard(() => updateParams({ tab: null, skill: 'new' }))}>
              New skill
            </Button>
          )}
        />

        {memoryStore ? (
          <Banner tone="warn" icon={AlertTriangle} title="Skills are not being saved">
            The API is running without Cosmos, so skills are held in memory and are lost when it restarts.
          </Banner>
        ) : null}

        {tab === 'review' ? (
          <div className="ak-split sk-split">
            <SkillList
              items={visible}
              counts={counts}
              status={status}
              onStatus={(v) => { if (v !== status) guard(() => updateParams({ status: v, skill: null })); }}
              query={query}
              onQuery={setQuery}
              kinds={kinds}
              onKinds={setKinds}
              kindOptions={kindOpts}
              selectedId={selectedId}
              onSelect={(id) => select(id)}
              onMove={onMove}
              lookups={lookups}
              loading={loading}
              error={loadError}
              onRetry={load}
              filtered={Boolean(query.trim() || kinds.length)}
              truncated={skills.length >= LIST_LIMIT}
            />
            <div className="ak-split-detail" ref={detailRef}>
              {renderDetail()}
            </div>
          </div>
        ) : (
          <ReflectionsTab
            onOpenSkill={(id) => updateParams({ tab: null, skill: id })}
            onSkillsChanged={load}
          />
        )}
      </AdminPage>

      <ConfirmDialog
        open={confirmDelete}
        title="Delete this skill?"
        danger
        busy={busyAction === 'delete'}
        confirmLabel="Delete permanently"
        onCancel={() => setConfirmDelete(false)}
        onConfirm={doDelete}
      >
        <p style={{ margin: 0 }}>
          <strong>{selected?.title}</strong> and its history are removed for good. This can&apos;t be undone.
          {selected?.status === 'rejected' ? ' If you keep it rejected instead, the assistant sees it and your reason when it reflects on that conversation again.' : ''}
        </p>
      </ConfirmDialog>

      <ConfirmDialog
        open={Boolean(pendingLeave)}
        title="Discard unsaved changes?"
        danger
        confirmLabel="Discard"
        onCancel={() => setPendingLeave(null)}
        onConfirm={() => {
          const fn = pendingLeave;
          setPendingLeave(null);
          setEdit(null);
          if (fn) fn();
        }}
      >
        <p style={{ margin: 0 }}>You have {plural(dirty.length, 'unsaved change')} to this skill.</p>
      </ConfirmDialog>
    </div>
  );
}
